import { cloneDeep } from "lodash";

import { colorVisionTestConfiguration } from "@constants/testConstants/testSpecificConstants/colorVisionConstants";
import { getVirtualAssistantVideoListForTest } from "@constants/virtualAssistanceConstants";
import color_vision_plates_en from "@data/color_vision_plates.json";
import {
  CollectedTestData,
  ColorVisionCollectedData,
  ColorVisionInitData,
  ColorVisionPlateResult,
  EyeType,
  TestCategory,
  TestConfiguration,
  TestType,
} from "types/testTypes";
import { ColorVisionCustomConfiguration } from "types/tests/specifics";
import {
  buildColorVisionPlateResult,
  translateColorVisionPlates,
} from "@utils/test-specific-utils/ColorVisionTestUtils";

type CurrentTestDetails = Record<string, unknown>;

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function parseColorVisionEye(value: unknown): EyeType | undefined {
  return typeof value === "string" && Object.values(EyeType).includes(value as EyeType)
    ? (value as EyeType)
    : undefined;
}

function normalizeColorVisionPlateResult(value: unknown): ColorVisionPlateResult | null {
  if (
    !isRecord(value) ||
    typeof value.plate !== "number" ||
    (typeof value.response !== "string" && typeof value.response !== "number")
  ) {
    return null;
  }
  return buildColorVisionPlateResult(
    value.plate,
    value.response,
    typeof value.responseTimeMs === "number" ? value.responseTimeMs : undefined,
    value.isCorrect,
  );
}

function extractColorVisionInitData(
  currentTestDetails: CurrentTestDetails,
): ColorVisionInitData | null {
  const rawResults = isRecord(currentTestDetails.results) ? currentTestDetails.results : null;
  const initSource = rawResults ?? currentTestDetails;
  const plateCount = initSource.plateCount;
  const isOperatorControlled = initSource.isOperatorControlled;

  if (typeof plateCount !== "number" || typeof isOperatorControlled !== "boolean") {
    return null;
  }

  return {
    plateCount,
    isOperatorControlled,
    initialized: true,
  };
}

function extractColorVisionResults(
  currentTestDetails: CurrentTestDetails,
): ColorVisionCollectedData {
  const rawResults = isRecord(currentTestDetails.results) ? currentTestDetails.results : null;
  const resultsSource = Array.isArray(rawResults?.responses)
    ? rawResults?.responses
    : Array.isArray(currentTestDetails.responses)
      ? currentTestDetails.responses
      : [];

  const collectedData: ColorVisionCollectedData = {};

  const initData = extractColorVisionInitData(currentTestDetails);
  if (initData) {
    collectedData.initData = initData;
  }

  const currentIndex =
    typeof rawResults?.currentIndex === "number"
      ? rawResults.currentIndex
      : typeof currentTestDetails.currentIndex === "number"
        ? currentTestDetails.currentIndex
        : null;

  if (currentIndex !== null && Number.isFinite(currentIndex)) {
    collectedData.currentIndex = currentIndex;
  }

  const results = resultsSource
    .map((result) => normalizeColorVisionPlateResult(result))
    .filter((result): result is ColorVisionPlateResult => result !== null);

  collectedData.results = results;

  if (results.length > 0) {
    collectedData.latestSnapshot = results[results.length - 1];
  }

  return collectedData;
}

export function buildColorVisionResumeConfiguration(
  currentTestDetails: CurrentTestDetails,
  existingConfiguration?: TestConfiguration | null,
): TestConfiguration {
  const configuration =
    existingConfiguration?.name === TestCategory.ColorVision
      ? cloneDeep(existingConfiguration)
      : cloneDeep(colorVisionTestConfiguration);
  const testType =
    currentTestDetails.testType === TestType.ColorVisionAutomatic
      ? TestType.ColorVisionAutomatic
      : TestType.ColorVision;

  configuration.testType = testType;
  const eyeType = parseColorVisionEye(currentTestDetails.eye);
  if (eyeType) {
    configuration.eyeConfiguration = {
      ...configuration.eyeConfiguration,
      eyeType,
    };
  }

  if (configuration.virtualAssistanceConfig) {
    configuration.virtualAssistanceConfig = {
      ...configuration.virtualAssistanceConfig,
      videoInfos: getVirtualAssistantVideoListForTest(configuration),
    };
  }

  const initDataSource = extractColorVisionInitData(currentTestDetails);

  if (initDataSource) {
    configuration.extraData = {
      ...configuration.extraData,
      plateCount: initDataSource.plateCount,
    };
  }

  const cvExtraData = configuration.extraData as ColorVisionCustomConfiguration | undefined;
  const sourcePlates = Array.isArray(cvExtraData?.questionsJson)
    ? cvExtraData.questionsJson
    : color_vision_plates_en;
  configuration.extraData = {
    ...configuration.extraData,
    questionsJson: translateColorVisionPlates(
      sourcePlates,
      configuration.virtualAssistanceConfig?.showAssistant
        ? configuration.virtualAssistanceConfig.assistantLanguage
        : null,
    ),
  };

  return configuration;
}

export function buildColorVisionResumeCollectedData(
  currentTestDetails: CurrentTestDetails,
): CollectedTestData {
  return {
    colorVisionData: extractColorVisionResults(currentTestDetails),
  };
}
